import {
  CreditCardType,
  MaskedCreditCardInfo,
  UserWithMediaAndGroups,
} from "@greenflagdate/shared";
import { useReq } from "@larner.dev/use-req";
import { Box, BoxProps, Image, Text } from "@chakra-ui/react";
import {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import { apiReq } from "../../lib/apiReq";
import { useStoreData } from "../../lib/store/store";
import { isLoggedIn } from "../../lib/isLoggedIn";
import { Loader } from "../Loader/Loader";

import AmericanExpress from "./images/AmericanExpress.png";
import MasterCard from "./images/MasterCard.png";
import Visa from "./images/Visa.png";
import Discover from "./images/Discover.png";
import { LinkButton } from "../LinkButton/LinkButton";
import { activeClientAdapter } from "../../lib/payments";
import { CardFieldsHandle } from "../../lib/payments/types";
import { getPaymentErrorMessage } from "../../lib/payments/getPaymentErrorMessage";

/** One logo per brand; a card with no `cardType` shows its number alone. */
const cardImages: Record<CreditCardType, string> = {
  AmericanExpress,
  MasterCard,
  Visa,
  Discover,
};

interface PaymentFormProps extends BoxProps {
  onSubmit?: () => void | Promise<void>;
  onEditCardChange?: (editing: boolean) => void;
  onLoadingStatusChange?: (val: boolean) => void;
}

export interface PaymentFormHandle {
  submitForm: () => void;
  reloadCard: () => void;
}

/**
 * The card on file, and the flow for replacing it.
 *
 * Everything processor-specific — the inputs, the validation, the SDK call
 * that turns a card into a token — belongs to the active client adapter. This
 * component only decides whether to show the stored card or the adapter's
 * entry UI, and hands whatever token comes back to the API.
 */
export const PaymentForm = forwardRef<PaymentFormHandle, PaymentFormProps>(
  ({ onSubmit, onLoadingStatusChange, onEditCardChange, ...props }, ref) => {
    const storeData = useStoreData();
    const adapter = useMemo(() => activeClientAdapter(), []);
    const fieldsRef = useRef<CardFieldsHandle>(null);
    const [serverError, setServerError] = useState<string>();
    const [editingCard, setEditingCard] = useState(false);
    const [getCardReq, getCardReqState] = useReq<MaskedCreditCardInfo | null>(
      apiReq
    );
    const loggedIn = useMemo(() => isLoggedIn(storeData), [storeData]);
    const [putCardReq] = useReq<UserWithMediaAndGroups>(apiReq);
    const editCardChange = useCallback(
      (editing: boolean) => {
        setEditingCard(editing);
        if (onEditCardChange) {
          onEditCardChange(editing);
        }
      },
      [onEditCardChange]
    );
    useEffect(() => {
      if (loggedIn) {
        getCardReq.get("/user/card").then((val) => {
          if (!val.success || (val.success && val.result === null)) {
            editCardChange(true);
          }
        });
      }
    }, [editCardChange, getCardReq, loggedIn]);
    // Warm the provider SDK on mount so submitting does not wait on a script.
    useEffect(() => {
      adapter.preload().catch(() => {
        /* noop — retried when the adapter tokenizes */
      });
    }, [adapter]);

    const submit = useCallback(async () => {
      if (
        !editingCard &&
        getCardReqState.result &&
        getCardReqState.result.postalCode
      ) {
        if (onSubmit) {
          await onSubmit();
        }
        return;
      }

      setServerError(undefined);
      // null means the adapter already rendered a field-level error.
      const tokenized = await fieldsRef.current?.tokenize();
      if (!tokenized) {
        return;
      }

      const response = await putCardReq.put("/user/card", {
        token: tokenized.token,
        postalCode: tokenized.postalCode,
      });
      if (response.success) {
        getCardReq.get("/user/card");
        if (onSubmit) {
          await onSubmit();
        }
        editCardChange(false);
      } else {
        setServerError(
          getPaymentErrorMessage(response.error.code, response.error.params)
        );
      }
    }, [
      editCardChange,
      editingCard,
      getCardReq,
      getCardReqState.result,
      onSubmit,
      putCardReq,
    ]);

    const cardLogo = getCardReqState.result?.cardType
      ? cardImages[getCardReqState.result.cardType]
      : undefined;

    useImperativeHandle(ref, () => ({
      submitForm: async () => {
        if (onLoadingStatusChange) {
          onLoadingStatusChange(true);
        }
        await submit();
        if (onLoadingStatusChange) {
          onLoadingStatusChange(false);
        }
      },
      reloadCard: async () => {
        const val = await getCardReq.get("/user/card");
        if (!val.success || (val.success && val.result === null)) {
          editCardChange(true);
        }
      },
    }));

    return (
      <Box {...props}>
        {!loggedIn ? (
          <Text fontSize="1rem">
            You must register / log in before entering your credit card
            information.
          </Text>
        ) : getCardReqState.loading ? (
          <Loader />
        ) : getCardReqState.result &&
          getCardReqState.result.postalCode &&
          !editingCard ? (
          <>
            <Text fontSize="0.75rem" marginBottom="0.25rem">
              CURRENT PAYMENT METHOD
            </Text>
            <Box display="flex" alignItems="center" gap="0.5rem">
              {cardLogo && <Image src={cardLogo} height="1.25rem" alt="" />}
              <Box>{getCardReqState.result.maskedCardNumber}</Box>
              <LinkButton onClick={() => editCardChange(true)}>
                Change Credit Card
              </LinkButton>
            </Box>
          </>
        ) : (
          <adapter.CardFields
            ref={fieldsRef}
            error={serverError}
            onCancel={
              getCardReqState.result && editingCard
                ? () => editCardChange(false)
                : undefined
            }
          />
        )}
      </Box>
    );
  }
);
